import { CustomLinkMarkdown } from "components/Form/components/Markdown/Markdown";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { Popover } from "components/Popover/Popover";
import RecordLabel from "components/RecordLabel";
import { getRecordColumnCellStyle } from "components/Table/recordColumnWidths";
import { TableColumn } from "components/Table/tableUtils";
import OverflowTooltip from "components/Tooltip/OverflowTooltip";
import { FeRecordLod, FeRecordLogRecord } from "config/config";
import { TimeAgo } from "components/TimeAgo/TimeAgo";
import {
    ReferenceItem,
    ReferenceListPopover,
} from "containers/Resources/screens/ResourcesSingle/components/RecordLogList/ReferenceList";
import { useEffect, useMemo, useRef, useState } from "react";
import { getRelativeUrl, getTypedLowerCase } from "utils/service";

export const ExpandableData = ({ data, uniqueKey }: { data: string; uniqueKey: string }) => {
    const previewRef = useRef<HTMLDivElement>(null);
    const [isClamped, setIsClamped] = useState(false);

    const isCode = useMemo(() => {
        const trimmedData = data.trim();

        // Match strings that start and end with triple backticks
        const markdownCodeBlockRegex = /^```[a-zA-Z0-9]*\n[\s\S]*?\n?```$/;
        return markdownCodeBlockRegex.test(trimmedData);
    }, [data]);

    const trimmedData = useMemo(() => {
        if (!isCode) {
            return data;
        }
        const trimmedData = data.trim();
        return trimmedData.replace(/```\n\n```/g, "").replace(/\n+```$/, "\n```");
    }, [isCode, data]);

    // Fixed-height log row (Stefan 2026-10-12, "data-table rows should be fixed
    // height everywhere. no exceptions. for the logs.data column (markdown) add a
    // pop-out affordance just as for custom object markdown attributes"). The Data
    // cell no longer grows the row in-flow; it renders the SAME clamped-preview +
    // markdown pop-out affordance used for read-only custom-object markdown
    // attribute cells (MarkdownExpandCell, AttributeValueSchema.tsx), reusing its
    // global classes so the two behave identically. The preview is clamped to the
    // uniform cell height (.attributeValueSchemaContainer--clamp, 3em); when it
    // actually overflows, a hover-revealed top-right expand icon opens a read-only
    // popover of the full markdown. This makes every log row one fixed height, so
    // the Logs list can be cleanly virtualized with off-viewport rows unmounted
    // (operator-task-fe-logs-tab-bounded-dom-virtualization, leg A).
    const renderMarkdown = () => (
        <CustomLinkMarkdown
            markdownString={trimmedData}
            className="statusMessageMarkdown statusMessageMarkdownColumn"
            uniqueKey={uniqueKey}
        />
    );

    // Show the expand affordance (and the gradient-fade truncation cue via the
    // --clamped modifier) ONLY when the clamped preview genuinely overflows, so a
    // short one-line log value renders with no icon — matching MarkdownExpandCell.
    useEffect(() => {
        const el = previewRef.current;
        if (!el) {
            return;
        }
        setIsClamped(el.scrollHeight - el.clientHeight > 1);
    }, [trimmedData]);

    return (
        <div
            className={`inlineEditPopoverCell markdownExpandCell${
                isClamped ? " markdownExpandCell--clamped" : ""
            }`}
        >
            <div
                ref={previewRef}
                className="attributeValueSchemaContainer--clamp inlineEditPopoverCell__preview"
            >
                {renderMarkdown()}
            </div>
            {isClamped && (
                <Popover
                    trigger={
                        <span
                            className="markdownExpandCell__expandTrigger"
                            aria-label="Expand"
                            title="Expand"
                        >
                            <Icon
                                byName="arrowExpand"
                                className="inlineEditPopoverCell__pencil markdownExpandCell__expandIcon"
                            />
                        </span>
                    }
                    toggleClassName="inlineEditPopoverCell__trigger"
                    closeOnInsideClick={false}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    contentClassName="markdownExpandPopover"
                >
                    <div className="markdownExpandPopover__body">{renderMarkdown()}</div>
                </Popover>
            )}
        </div>
    );
};
const baseLogColumns: TableColumn[] = [
    {
        label: "Created at",
        key: "created_at",
        shown: true,
        sortable: true,
        // Refresh the relative "X ago" label every 5s rather than the app-wide 60s
        // throttle, so a seconds-old log timestamp keeps ticking while the user
        // watches the list statically instead of freezing until the next scroll
        // re-render (Stefan, 2026-10-15 Logs feedback). Cheap here because the Logs
        // list is windowed — only the near-viewport rows are mounted, so the churn
        // the 60s default guards against does not apply. TimeAgo is rendered
        // directly (not via config/utils' createdAtDto) so this module does not
        // depend on config/utils' late exports through the config import cycle.
        dto: ({ created_at }: FeRecordLod) =>
            created_at ? <TimeAgo datetime={created_at} minInterval={5} /> : null,
    },
    {
        label: "Subject",
        key: "subject",
        shown: true,
        sortable: false,
        // Cap the column width (min/max) so the subject renders on a SINGLE line and
        // ellipsizes to the column width instead of wrapping to a second line, and so
        // a long subject can't push the whole table into a page-level horizontal scroll.
        cellStyle: getRecordColumnCellStyle("subject"),
        dto: ({ subject }: FeRecordLod) => {
            if (!subject) {
                return null;
            }
            // Single-line, CSS-ellipsis truncation that follows the actual rendered
            // column width (the same OverflowTooltip primitive the RecordLabel name
            // cell uses in resource lists): the "…" is applied by CSS to fit the
            // column, and the full subject is revealed on hover ONLY when it is
            // actually truncated. Widening the column reveals more text dynamically.
            return (
                <OverflowTooltip
                    overlay={subject}
                    overlayStyle={{ maxWidth: "800px" }}
                    isOverlapping={false}
                >
                    {subject}
                </OverflowTooltip>
            );
        },
    },
    {
        label: "References",
        headerRenderer: () => <Icon icon={IconCollection.link} />,
        key: "references",
        shown: true,
        sortable: false,
        dto: ({
            id,
            record_log_record_plain_list_record_log_id,
        }: {
            id: string;
            record_log_record_plain_list_record_log_id: FeRecordLogRecord[];
        }) => {
            if (!record_log_record_plain_list_record_log_id?.length) {
                return null;
            }
            if (record_log_record_plain_list_record_log_id.length === 1) {
                return (
                    <ReferenceItem
                        referenceRecord={record_log_record_plain_list_record_log_id[0]}
                    />
                );
            } else {
                return <ReferenceListPopover id={id} />;
            }
        },
    },
    {
        label: "Data",
        key: "data",
        shown: true,
        sortable: false,
        dto: ({ data, id }: FeRecordLod) => {
            return data ? <ExpandableData data={data} uniqueKey={id} /> : null;
        },
    },
];

const recordColumn: TableColumn[] = [
    {
        label: "Record",
        key: "record",
        shown: true,
        sortable: false,
        dto: ({ record_id_record }: FeRecordLod) => {
            if (record_id_record == null) return null;
            const { name, record_type, id } = record_id_record;

            return (
                <RecordLabel
                    key={id}
                    link={getRelativeUrl(getTypedLowerCase(record_type), id)}
                    name={name}
                    iconByType={getTypedLowerCase(record_type)}
                />
            );
        },
    },
];

const allLogColumns = [...baseLogColumns];
allLogColumns.splice(1, 0, ...recordColumn);

export { allLogColumns, baseLogColumns };
